/**
 * Category Input Component
 * 
 * Reusable category selection with recent chips and full category selector.
 * Memoized to prevent re-renders when other form fields change.
 */

import { useState, memo } from 'react';
import { AlertCircle } from 'lucide-react';
import type { CategoryViewModel, CreateCategoryDto } from '@/lib/domains';
import CategorySelector from '@/components/category-selector';

interface CategoryInputProps {
    categories: CategoryViewModel[];
    selectedCategoryId: string;
    recentCategoryIds: string[];
    onSelect: (categoryId: string) => void;
    onCreateCategory: (dto: CreateCategoryDto) => Promise<void>;
    error?: string;
    suggestedCategory?: CategoryViewModel;
    loadingCategories?: boolean;
}

function CategoryInputComponent({
    categories,
    selectedCategoryId,
    recentCategoryIds,
    onSelect,
    onCreateCategory,
    error,
    suggestedCategory,
    loadingCategories = false
}: CategoryInputProps) {
    const [showCategorySelector, setShowCategorySelector] = useState(false);

    const selectedCategory = categories.find(c => c.id === selectedCategoryId);

    const handleCreateCategory = async (dto: CreateCategoryDto) => {
        await onCreateCategory(dto);
        setShowCategorySelector(false);
    };

    const handleSelectCategory = (category: CategoryViewModel) => {
        onSelect(category.id);
        setShowCategorySelector(false);
    };

    return (
        <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
                Category *
                {suggestedCategory && (
                    <span className="ml-2 text-xs text-teal-600 font-normal">
                        (Suggested: {suggestedCategory.displayName})
                    </span>
                )}
            </label>

            {/* Recent categories as chips */}
            {!showCategorySelector && recentCategoryIds.length > 0 && (
                <div className="mb-3 flex flex-wrap gap-2">
                    {recentCategoryIds.slice(0, 4).map(catId => {
                        const cat = categories.find(c => c.id === catId);
                        if (!cat) return null;
                        return (
                            <button
                                key={cat.id}
                                type="button"
                                onClick={() => onSelect(cat.id)}
                                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${selectedCategoryId === cat.id
                                    ? 'bg-teal-100 text-teal-700 border border-teal-300'
                                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-200'
                                    }`}
                            >
                                <span>{cat.icon}</span>
                                <span>{cat.displayName}</span>
                            </button>
                        );
                    })}
                </div>
            )}

            {loadingCategories ? (
                <div className="text-center py-4 text-gray-500">Loading categories...</div>
            ) : showCategorySelector ? (
                <div className="border border-gray-200 rounded-lg p-4">
                    <CategorySelector
                        categories={categories}
                        selectedCategoryId={selectedCategoryId}
                        onSelect={handleSelectCategory}
                        onCreateCategory={handleCreateCategory}
                        recentCategoryIds={recentCategoryIds}
                        showSearch={true}
                        columns={4}
                    />
                </div>
            ) : (
                <button
                    type="button"
                    onClick={() => setShowCategorySelector(true)}
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 text-left hover:border-gray-400 transition-colors flex items-center gap-3"
                >
                    {selectedCategory ? (
                        <>
                            <span className="text-2xl">{selectedCategory.icon}</span>
                            <span className="font-medium">{selectedCategory.displayName}</span>
                        </>
                    ) : (
                        <span className="text-gray-500">Select a category</span>
                    )}
                </button>
            )}
            {error && (
                <p className="text-red-500 text-sm mt-2 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {error}
                </p>
            )}
        </div>
    );
}

/**
 * Memoized version prevents re-renders when other form fields change.
 * Categories array is compared by reference.
 */
export const CategoryInput = memo(CategoryInputComponent);
